import React, { useEffect, useState } from "react";
import apiService from "../../services/apiService";
import {
  Modal,
  Input,
  Button,
  Spin,
  Alert,
  Row,
  Col,
  message,
  Typography,
} from "antd";
import {
  SafetyCertificateOutlined,
  CopyOutlined,
  CheckOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  LockOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import "./ForceMfaModal.css";

const { Title, Paragraph } = Typography;

export default function ForceMfaModal({ open, onComplete, onLogout }) {
  // ── States ─────────────────────────────────────────────────────────────────
  const [step, setStep]                   = useState("setup"); // 'setup' | 'backup_codes'
  const [loading, setLoading]             = useState(false);
  const [submitting, setSubmitting]       = useState(false);
  const [qrCode, setQrCode]               = useState(null);
  const [secret, setSecret]               = useState(null);
  const [otp, setOtp]                     = useState("");
  const [otpError, setOtpError]           = useState("");
  const [backupCodes, setBackupCodes]     = useState([]);
  const [copiedSecret, setCopiedSecret]   = useState(false);
  const [copiedCodes, setCopiedCodes]     = useState(false);

  // Fetch setup details when modal opens
  useEffect(() => {
    if (open) {
      setStep("setup");
      setOtp("");
      setOtpError("");
      setBackupCodes([]);
      setCopiedSecret(false);
      setCopiedCodes(false);
      fetchSetup();
    }
  }, [open]);

  const fetchSetup = async () => {
    setLoading(true);
    try {
      const response = await apiService.get("auth/mfa/setup");
      if (response.status === 200) {
        setQrCode(response.data.qr_code);
        setSecret(response.data.secret);
      }
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to initialize 2FA setup. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Verify Setup OTP ───────────────────────────────────────────────────────
  const handleVerify = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const cleanOtp = otp.trim();
    if (cleanOtp.length !== 6) {
      setOtpError("Please enter the 6-digit code from your authenticator app.");
      return;
    }

    setSubmitting(true);
    setOtpError("");
    try {
      const response = await apiService.post("auth/mfa/verify-setup", { otp: cleanOtp });
      if (response.status === 200) {
        message.success("Two-Factor Authentication enabled successfully!");
        setBackupCodes(response.data.backup_codes || []);
        setStep("backup_codes");
      }
    } catch (error) {
      if (error.response?.status === 422) {
        setOtpError(error.response.data.errors?.otp?.message || "Invalid OTP code. Please try again.");
      } else {
        message.error(error.response?.data?.message || "Failed to verify code. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Copy Secret Key ────────────────────────────────────────────────────────
  const copySecretKey = () => {
    if (secret) {
      navigator.clipboard.writeText(secret);
      setCopiedSecret(true);
      message.success("Secret key copied to clipboard!");
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  // ── Copy All Backup Codes ──────────────────────────────────────────────────
  const copyAllBackupCodes = () => {
    if (backupCodes.length > 0) {
      navigator.clipboard.writeText(backupCodes.join("\n"));
      setCopiedCodes(true);
      message.success("Recovery codes copied to clipboard!");
      setTimeout(() => setCopiedCodes(false), 2000);
    }
  };

  // ── Download Backup Codes as Text File ─────────────────────────────────────
  const downloadBackupCodes = () => {
    const userStr = localStorage.getItem("user");
    let userEmail = "admin";
    if (userStr) {
      try {
        const u = JSON.parse(userStr);
        userEmail = u.email || "admin";
      } catch (err) {}
    }
    const content = `RSA Admin - Emergency Two-Factor Authentication Recovery Codes\nAccount: ${userEmail}\nDate: ${new Date().toLocaleString()}\n\nKeep these codes safe. Each code can be used once if you lose access to your authenticator app (Google Authenticator, Microsoft Authenticator, etc.):\n\n${backupCodes.map((c, i) => `${i + 1}. ${c}`).join("\n")}\n\nImportant: Store this file in a secure, confidential place.`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `rsa-admin-recovery-codes-${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success("Recovery codes downloaded!");
  };

  // ── Complete Setup and Enter Dashboard ─────────────────────────────────────
  const handleFinish = () => {
    // Update local user object so mfa_enabled is true
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const userObj = JSON.parse(userStr);
        userObj.mfa_enabled = true;
        localStorage.setItem("user", JSON.stringify(userObj));
      } catch (e) {}
    }

    if (onComplete) {
      onComplete();
    }
  };

  return (
    <Modal
      open={open}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      width={460}
      wrapClassName="force-mfa-modal"
      rootClassName="force-mfa-modal"
      className="force-mfa-modal"
      footer={null}
      destroyOnClose
    >
      <div style={{ textAlign: "center" }}>
        {/* Header Icon & Title */}
        <div className="force-mfa-badge">
          <SafetyCertificateOutlined />
        </div>

        <Title level={4} className="force-mfa-title">
          {step === "setup"
            ? "Two-Factor Authentication Setup Required"
            : "Save Your Emergency Recovery Codes"}
        </Title>

        <Paragraph className="force-mfa-desc">
          {step === "setup"
            ? "Security policy requires all administrative accounts to protect their logins with an authenticator app (such as Google Authenticator or Microsoft Authenticator) before accessing the portal."
            : "MFA is now active! Store these one-time recovery codes in a safe place. You will need them if you ever lose your device."}
        </Paragraph>

        {/* ── STEP 1: Scan QR & Verify OTP ── */}
        {step === "setup" && (
          <Spin spinning={loading}>
            {/* QR Code Container */}
            {qrCode && (
              <div className="force-mfa-qr-card">
                <img
                  src={qrCode}
                  alt="2FA QR Code"
                  className="force-mfa-qr-img"
                />
              </div>
            )}

            {/* Manual Secret Key (Responsive, Wrap-Safe, No Overflow) */}
            {secret && (
              <div className="force-mfa-secret-box">
                <span className="force-mfa-secret-label">
                  Cannot scan the QR code? Enter this key manually:
                </span>
                <div className="force-mfa-secret-row">
                  <span className="force-mfa-secret-code">{secret}</span>
                  <Button
                    size="small"
                    icon={copiedSecret ? <CheckOutlined /> : <CopyOutlined />}
                    onClick={copySecretKey}
                    className="force-mfa-copy-btn"
                  >
                    {copiedSecret ? "Copied" : "Copy"}
                  </Button>
                </div>
              </div>
            )}

            {/* Verification Form */}
            <form onSubmit={handleVerify} className="force-mfa-otp-section">
              <label className="force-mfa-otp-label">
                Enter 6-digit code from your authenticator app:
              </label>
              <Input
                placeholder="000 000"
                maxLength={6}
                value={otp}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setOtp(val);
                  setOtpError("");
                }}
                className="force-mfa-otp-input"
                autoFocus
                inputMode="numeric"
                autoComplete="one-time-code"
                status={otpError ? "error" : ""}
              />
              {otpError && (
                <div className="force-mfa-otp-error">
                  {otpError}
                </div>
              )}

              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={submitting}
                disabled={otp.length !== 6 || loading}
                icon={<LockOutlined />}
                className="force-mfa-primary-btn"
                style={{ marginTop: 14 }}
              >
                Verify & Activate MFA
              </Button>
            </form>
          </Spin>
        )}

        {/* ── STEP 2: Backup Recovery Codes Display ── */}
        {step === "backup_codes" && (
          <div>
            <Alert
              message="Important: Store these codes safely"
              description="Each recovery code can be used once to access your account if your authenticator device is lost or unavailable. These codes will not be shown again."
              type="warning"
              showIcon
              style={{ marginBottom: 14, textAlign: "left", borderRadius: 8 }}
            />

            <div className="force-mfa-backup-box">
              <Row gutter={[8, 8]}>
                {backupCodes.map((code, idx) => (
                  <Col xs={12} sm={12} key={idx}>
                    <div className="force-mfa-code-badge">
                      {code}
                    </div>
                  </Col>
                ))}
              </Row>
            </div>

            <div className="force-mfa-actions-row">
              <Button
                icon={copiedCodes ? <CheckOutlined /> : <CopyOutlined />}
                onClick={copyAllBackupCodes}
              >
                {copiedCodes ? "Copied Codes" : "Copy Codes"}
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={downloadBackupCodes}
              >
                Download .txt File
              </Button>
            </div>

            <Button
              type="primary"
              size="large"
              block
              icon={<CheckCircleOutlined />}
              onClick={handleFinish}
              className="force-mfa-finish-btn"
            >
              I Have Saved My Codes — Enter Dashboard →
            </Button>
          </div>
        )}

        {/* Emergency Logout Action */}
        {onLogout && (
          <div style={{ marginTop: 14 }}>
            <Button
              type="link"
              size="small"
              icon={<LogoutOutlined />}
              onClick={onLogout}
              className="force-mfa-logout-link"
            >
              Sign out and setup later
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
